<template>
  <div>
    <div class="page-header">
      <h1>🏦 資產分析</h1>
      <p>歷年資產變化與支出結構</p>
    </div>

    <div v-if="loading" class="empty-state">
      <div class="spinner"></div>
      <p>資料載入中...</p>
    </div>

    <template v-else-if="hasData">

      <!-- 資產總覽卡 -->
      <div class="stats-row">
        <div class="stat-card highlight">
          <div class="stat-label">目前總資產</div>
          <div class="stat-value">${{ formatNum(currentTotal) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">YoY 成長（2025→2026）</div>
          <div class="stat-value" :class="yoy2526 >= 0 ? 'positive' : 'negative'">
            {{ yoy2526 >= 0 ? '+' : '' }}${{ formatNum(yoy2526) }}
            <span class="stat-pct">{{ yoy2526pct }}</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">YoY 成長（2024→2025）</div>
          <div class="stat-value positive">+${{ formatNum(yoy2425) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">月均支出（近兩年）</div>
          <div class="stat-value expense">${{ formatNum(avgMonthlyExpense) }}</div>
        </div>
      </div>

      <div class="charts-grid">

        <!-- 1. 年度資產趨勢 -->
        <div class="chart-card chart-card-wide">
          <div class="chart-title">📈 歷年總資產</div>
          <div class="chart-subtitle">2023 ~ 2026 年度末資產總值</div>
          <v-chart class="chart chart-bar" :option="yearlyTotalOption" autoresize />
        </div>

        <!-- 2. 當前資產配置 -->
        <div class="chart-card">
          <div class="chart-title">💰 當前資產配置</div>
          <div class="chart-subtitle">各帳戶現值佔比（正資產）</div>
          <v-chart class="chart" :option="assetPieOption" autoresize />
        </div>

        <!-- 3. 年度支出類別 -->
        <div class="chart-card">
          <div class="chart-title">🏷️ 本年度支出類別</div>
          <div class="chart-subtitle">{{ currentYear }} 年支出分布</div>
          <v-chart class="chart" :option="yearlyExpenseOption" autoresize />
        </div>

        <!-- 4. 月支出趨勢 -->
        <div class="chart-card chart-card-wide">
          <div class="chart-title">📉 每月支出趨勢</div>
          <div class="chart-subtitle">歷史月支出（不含投資損失）</div>
          <v-chart class="chart chart-tall" :option="monthlyExpenseOption" autoresize />
        </div>

        <!-- 5. 負債明細 -->
        <div v-if="liabilities.length > 0" class="chart-card">
          <div class="chart-title">💳 負債明細</div>
          <div class="chart-subtitle">信用卡及負值帳戶</div>
          <div class="liab-list">
            <div v-for="item in liabilities" :key="item[0]" class="liab-row">
              <span class="liab-name">{{ item[0] }}</span>
              <span class="liab-amount">${{ formatNum(Math.abs(parseNum(item[1]))) }}</span>
            </div>
            <div class="liab-total">
              <span>合計負債</span>
              <span class="liab-amount negative">${{ formatNum(totalLiability) }}</span>
            </div>
          </div>
        </div>

        <!-- 6. 正資產 Top 排行 -->
        <div class="chart-card">
          <div class="chart-title">🏆 資產排行</div>
          <div class="chart-subtitle">各帳戶正資產金額</div>
          <v-chart class="chart" :option="assetBarOption" autoresize />
        </div>

        <!-- 7. 資產明細表格 -->
        <div class="chart-card chart-card-wide">
          <div class="chart-title">📋 資產明細</div>
          <div class="chart-subtitle">各帳戶現值一覽（現值為 0 的帳戶已隱藏）</div>
          <div class="table-scroll">
            <table>
              <thead>
                <tr>
                  <th class="th-sort" @click="assetSort('name')">
                    資產 <span class="sort-icon">{{ assetSortIcon('name') }}</span>
                  </th>
                  <th class="th-sort text-right" @click="assetSort('value')">
                    現值 <span class="sort-icon">{{ assetSortIcon('value') }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in sortedAssets" :key="i"
                    :class="{ 'row-liability': parseNum(row[1]) < 0 }">
                  <td>{{ row[0] }}</td>
                  <td class="text-right"
                      :class="parseNum(row[1]) < 0 ? 'cell-negative' : 'cell-positive'">
                    ${{ formatNum(parseNum(row[1])) }}
                  </td>
                </tr>
                <tr class="total-row">
                  <td><strong>合計</strong></td>
                  <td class="text-right">
                    <strong :class="currentTotal >= 0 ? 'cell-positive' : 'cell-negative'">
                      ${{ formatNum(currentTotal) }}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </template>

    <div v-else class="empty-state">
      <div class="empty-icon">📭</div>
      <h3>載入失敗</h3>
      <p>無法取得資產資料，請確認連線</p>
    </div>
  </div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart, BarChart, LineChart } from 'echarts/charts';
import {
  TitleComponent, TooltipComponent, LegendComponent,
  GridComponent, MarkLineComponent
} from 'echarts/components';
import VChart from 'vue-echarts';
import axios from 'axios';
axios.defaults.baseURL = 'https://mb.kman.idv.tw:9579/';

use([
  CanvasRenderer, PieChart, BarChart, LineChart,
  TitleComponent, TooltipComponent, LegendComponent,
  GridComponent, MarkLineComponent
]);

const PALETTE = [
  '#4f46e5','#7c3aed','#0891b2','#059669','#d97706',
  '#dc2626','#db2777','#ea580c','#2563eb','#0d9488',
  '#65a30d','#ca8a04','#9333ea','#e11d48','#0284c7'
];

export default {
  components: { VChart },
  data() {
    return {
      loading: false,
      assets: [],          // current view: [[name, value], ...]
      yearlyTotals: [],    // [[year, amount], ...]
      monthlyExpenses: [], // [[date, amount], ...]
      yearlyData: [],      // [[category, amount, pct], ...]
      currentYear: new Date().getFullYear(),
      assetSortCol: 'value',
      assetSortDir: 'desc'
    };
  },
  computed: {
    hasData() {
      return this.assets.length > 0;
    },

    // 正資產 / 負資產
    positiveAssets() {
      return this.assets.filter(r => this.parseNum(r[1]) > 0);
    },
    liabilities() {
      return this.assets.filter(r => this.parseNum(r[1]) < 0);
    },

    currentTotal() {
      return this.assets.reduce((s, r) => s + this.parseNum(r[1]), 0);
    },
    totalLiability() {
      return Math.abs(this.liabilities.reduce((s, r) => s + this.parseNum(r[1]), 0));
    },

    // YoY
    yoy2526() {
      const y25 = this.findYearly(2025), y26 = this.findYearly(2026);
      return y26 - y25;
    },
    yoy2526pct() {
      const y25 = this.findYearly(2025);
      if (!y25) return '';
      return `(${(this.yoy2526 / y25 * 100).toFixed(1)}%)`;
    },
    yoy2425() {
      return this.findYearly(2025) - this.findYearly(2024);
    },

    sortedAssets() {
      const rows = this.assets.filter(r => this.parseNum(r[1]) !== 0);
      return [...rows].sort((a, b) => {
        let cmp;
        if (this.assetSortCol === 'name') {
          cmp = String(a[0]).localeCompare(String(b[0]), 'zh-TW');
        } else {
          cmp = this.parseNum(a[1]) - this.parseNum(b[1]);
        }
        return this.assetSortDir === 'asc' ? cmp : -cmp;
      });
    },

    avgMonthlyExpense() {
      if (!this.monthlyExpenses.length) return 0;
      const total = this.monthlyExpenses.reduce((s, r) => s + this.parseNum(r[1]), 0);
      return Math.round(total / this.monthlyExpenses.length);
    },

    // ── 圖表 Options ──

    // 1. 年度資產折線+長條
    yearlyTotalOption() {
      const years  = this.yearlyTotals.map(r => String(r[0]));
      const values = this.yearlyTotals.map(r => this.parseNum(r[1]));
      const growths = values.map((v, i) => i === 0 ? null : v - values[i-1]);

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const lines = params.map(p =>
              `${p.marker}${p.seriesName}：$${Math.round(p.value || 0).toLocaleString()}`
            );
            return `${params[0].axisValue} 年<br/>${lines.join('<br/>')}`;
          }
        },
        legend: { data: ['總資產','YoY 成長'], top: 0, textStyle: { fontSize: 12, color: '#374151' } },
        grid: { left: 16, right: 16, top: 40, bottom: 0, containLabel: true },
        xAxis: { type: 'category', data: years, axisLabel: { fontSize: 13, color: '#374151' }, axisTick: { show: false } },
        yAxis: [
          {
            type: 'value', name: '總資產',
            axisLabel: { formatter: v => `$${(v/1e6).toFixed(1)}M`, fontSize: 11, color: '#9ca3af' },
            splitLine: { lineStyle: { color: '#f3f4f6' } }
          },
          {
            type: 'value', name: 'YoY 成長', position: 'right',
            axisLabel: { formatter: v => `$${(v/1e6).toFixed(1)}M`, fontSize: 11, color: '#9ca3af' },
            splitLine: { show: false }
          }
        ],
        color: ['#4f46e5','#059669'],
        series: [
          {
            name: '總資產', type: 'bar', data: values, yAxisIndex: 0, barMaxWidth: 60,
            itemStyle: { borderRadius: [6,6,0,0], color: '#4f46e5' },
            label: { show: true, position: 'top', formatter: p => `$${(p.value/1e6).toFixed(2)}M`, fontSize: 12, color: '#374151' }
          },
          {
            name: 'YoY 成長', type: 'line', data: growths, yAxisIndex: 1,
            symbol: 'circle', symbolSize: 8,
            lineStyle: { color: '#059669', width: 2.5 },
            itemStyle: { color: '#059669' },
            label: {
              show: true, position: 'top',
              formatter: p => p.value != null ? `+$${(p.value/1e6).toFixed(2)}M` : '',
              fontSize: 11, color: '#059669'
            }
          }
        ]
      };
    },

    // 2. 資產配置甜甜圈
    assetPieOption() {
      const data = this.positiveAssets
        .map(r => ({ name: r[0], value: this.parseNum(r[1]) }))
        .sort((a, b) => b.value - a.value);

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          formatter: p => `${p.name}<br/>$${p.value.toLocaleString()}　${p.percent}%`
        },
        legend: {
          orient: 'vertical', right: 8, top: 'center',
          textStyle: { fontSize: 11, color: '#374151' },
          formatter: name => name.length > 7 ? name.substring(0, 7) + '…' : name
        },
        color: PALETTE,
        series: [{
          type: 'pie', radius: ['38%', '65%'], center: ['38%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
          label: {
            show: true, position: 'center',
            formatter: () => `$${(this.currentTotal / 1e6).toFixed(2)}M`,
            fontSize: 15, fontWeight: 'bold', color: '#1f2937'
          },
          emphasis: {
            label: { show: true, formatter: p => `${p.name}\n${p.percent}%`, fontSize: 13 },
            itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' }
          },
          data
        }]
      };
    },

    // 3. 年度支出類別玫瑰圖
    yearlyExpenseOption() {
      const data = this.yearlyData.map(r => ({
        name: r[0],
        value: this.parseNum(r[1])
      }));

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          formatter: p => `${p.name}<br/>$${p.value.toLocaleString()}　${p.percent}%`
        },
        legend: {
          orient: 'vertical', right: 8, top: 'center',
          textStyle: { fontSize: 11, color: '#374151' },
          formatter: name => name.length > 5 ? name.substring(0, 5) + '…' : name
        },
        color: PALETTE,
        series: [{
          type: 'pie', radius: ['18%', '62%'], center: ['38%', '50%'],
          roseType: 'area',
          itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: 13, fontWeight: 'bold' },
            itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' }
          },
          data
        }]
      };
    },

    // 4. 月支出折線圖
    monthlyExpenseOption() {
      const labels  = this.monthlyExpenses.map(r => String(r[0]).substring(0, 7));
      const values  = this.monthlyExpenses.map(r => this.parseNum(r[1]));
      const avg     = this.avgMonthlyExpense;

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const p = params[0];
            return `${p.axisValue}<br/>${p.marker}支出：$${p.value.toLocaleString()}`;
          }
        },
        grid: { left: 16, right: 16, top: 20, bottom: 0, containLabel: true },
        xAxis: {
          type: 'category', data: labels, boundaryGap: false,
          axisLabel: { rotate: 45, fontSize: 10, color: '#9ca3af',
            formatter: v => v.substring(2) // 去掉年份前兩位，省空間
          },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: v => `$${(v/1000).toFixed(0)}k`, fontSize: 11, color: '#9ca3af' },
          splitLine: { lineStyle: { color: '#f3f4f6' } }
        },
        color: ['#dc2626'],
        series: [{
          type: 'line', data: values, name: '月支出',
          smooth: true, symbol: 'circle', symbolSize: 5,
          lineStyle: { color: '#dc2626', width: 2 },
          itemStyle: { color: '#dc2626' },
          areaStyle: {
            color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(220,38,38,0.2)' },
                { offset: 1, color: 'rgba(220,38,38,0.02)' }
              ]
            }
          },
          markLine: {
            silent: true,
            data: [{ yAxis: avg, name: '月均' }],
            lineStyle: { color: '#d97706', type: 'dashed', width: 1.5 },
            label: { formatter: `月均 $${avg.toLocaleString()}`, color: '#d97706', fontSize: 11 }
          }
        }]
      };
    },

    // 5. 資產排行橫條
    assetBarOption() {
      const sorted = [...this.positiveAssets]
        .map(r => ({ name: r[0], value: this.parseNum(r[1]) }))
        .sort((a, b) => a.value - b.value);

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: p => `${p[0].name}：$${p[0].value.toLocaleString()}`
        },
        grid: { left: 80, right: 20, top: 10, bottom: 10, containLabel: false },
        xAxis: {
          type: 'value',
          axisLabel: { formatter: v => `$${(v/1e6).toFixed(1)}M`, fontSize: 10, color: '#9ca3af' },
          splitLine: { lineStyle: { color: '#f3f4f6' } }
        },
        yAxis: {
          type: 'category', data: sorted.map(d => d.name),
          axisLabel: { fontSize: 11, color: '#374151' }, axisTick: { show: false }
        },
        color: PALETTE,
        series: [{
          type: 'bar', barMaxWidth: 22,
          data: sorted.map((d, i) => ({
            value: d.value,
            itemStyle: { color: PALETTE[i % PALETTE.length], borderRadius: [0,4,4,0] }
          })),
          label: {
            show: true, position: 'right',
            formatter: p => `$${(p.value/1e4).toFixed(0)}萬`,
            fontSize: 10, color: '#6b7280'
          }
        }]
      };
    }
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    assetSort(col) {
      if (this.assetSortCol === col) {
        this.assetSortDir = this.assetSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.assetSortCol = col;
        this.assetSortDir = col === 'value' ? 'desc' : 'asc';
      }
    },
    assetSortIcon(col) {
      if (this.assetSortCol !== col) return '↕';
      return this.assetSortDir === 'asc' ? '↑' : '↓';
    },
    parseNum(v) {
      if (v === null || v === undefined || v === '') return 0;
      if (typeof v === 'number') return v;
      const str = String(v).replace(/[$,]/g, '').trim();
      const n = parseFloat(str);
      return isNaN(n) ? 0 : n;
    },
    formatNum(v) {
      return Math.round(Math.abs(v)).toLocaleString();
    },
    findYearly(yr) {
      const row = this.yearlyTotals.find(r => r[0] === yr);
      return row ? this.parseNum(row[1]) : 0;
    },
    async loadAll() {
      this.loading = true;
      const headers = { Authorization: `Bearer ${this.$store.getters.setToken}` };
      try {
        const [assetsRes, yearlyRes, monthlyRes, yearlyDataRes] = await Promise.all([
          axios.get('getassets', { headers }),
          axios.get('getyearlytotal', { headers }),
          axios.get('getmonthlyexpense', { headers }),
          axios.get('getyearlydata', { headers })
        ]);
        this.assets          = assetsRes.data.data || [];
        this.yearlyTotals    = yearlyRes.data.data || [];
        this.monthlyExpenses = monthlyRes.data.data || [];
        this.yearlyData      = yearlyDataRes.data.data || [];
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    }
  }
};
</script>

<style scoped>
.page-header { margin-bottom: 20px; }
.page-header h1 { font-size: 24px; font-weight: 700; }
.page-header p  { font-size: 14px; color: var(--text-light); margin-top: 2px; }

/* Stats */
.stats-row {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px;
}
.stat-card {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 14px 16px; text-align: center;
}
.stat-card.highlight { border: 2px solid var(--primary); }
.stat-label { font-size: 12px; color: var(--text-light); margin-bottom: 4px; }
.stat-value { font-size: 18px; font-weight: 700; color: var(--text); }
.stat-value.positive { color: #059669; }
.stat-value.negative { color: #dc2626; }
.stat-value.expense  { color: #dc2626; }
.stat-pct { font-size: 13px; color: #9ca3af; font-weight: 400; margin-left: 4px; }

/* Charts grid */
.charts-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;
}
.chart-card {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 16px 20px;
}
.chart-card-wide { grid-column: span 2; }
.chart-title   { font-size: 15px; font-weight: 700; color: var(--text); }
.chart-subtitle { font-size: 12px; color: var(--text-light); margin-bottom: 8px; }
.chart      { height: 300px; }
.chart-bar  { height: 240px; }
.chart-tall { height: 280px; }

/* Liability list */
.liab-list { margin-top: 8px; }
.liab-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 14px;
}
.liab-name { color: var(--text); }
.liab-amount { font-weight: 600; color: #dc2626; }
.liab-total {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 0 0; font-size: 14px; font-weight: 700;
}
.negative { color: #dc2626; }

/* Asset table */
.table-scroll { overflow-x: auto; margin-top: 8px; }
table { width: 100%; border-collapse: collapse; }
thead { background: #f9fafb; }
th {
  padding: 10px 14px; font-size: 12px; font-weight: 600;
  color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px;
  text-align: left; border-bottom: 1px solid var(--border);
}
td { padding: 10px 14px; font-size: 14px; border-bottom: 1px solid #f3f4f6; }
tr:hover { background: #fafaff; }
.text-right { text-align: right; }
.th-sort { cursor: pointer; user-select: none; transition: color 0.15s, background 0.15s; }
.th-sort:hover { color: var(--primary); background: #eef2ff; }
.sort-icon { font-size: 11px; opacity: 0.5; margin-left: 2px; }
.th-sort:hover .sort-icon { opacity: 1; color: var(--primary); }
.cell-positive { color: #059669; font-weight: 600; }
.cell-negative { color: #dc2626; font-weight: 600; }
.row-liability { background: #fff8f8; }
.total-row { background: #f9fafb; border-top: 2px solid var(--border); }
.total-row td { padding: 12px 14px; }

/* Empty */
.empty-state {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 48px 20px; text-align: center;
}
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-state h3 { font-size: 18px; font-weight: 600; margin-bottom: 4px; }
.empty-state p  { font-size: 14px; color: var(--text-light); }

.spinner {
  width: 32px; height: 32px; border: 3px solid var(--border);
  border-top-color: var(--primary); border-radius: 50%;
  animation: spin 0.8s linear infinite; margin: 0 auto 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .stats-row { grid-template-columns: repeat(2, 1fr); }
  .charts-grid { grid-template-columns: 1fr; }
  .chart-card-wide { grid-column: span 1; }
  .chart { height: 260px; }
  .chart-bar { height: 200px; }
  .chart-tall { height: 240px; }
}
</style>
